// Admin worked example for the departure pickup calculator. Mirrors gt_departure_plan (migration 285) on a wall clock:
// pickup = flight - airport cushion - (zone base minutes + traffic add-on), rounded DOWN. The traffic window is the one the van is
// on the road in: try the no-traffic start, add that window, re-check once from the earlier start, keep the larger add-on.
export const DAY_GROUPS = [{ key: 'mon_thu', label: 'Mon–Thu' }, { key: 'fri_sun', label: 'Fri–Sun' }];
export const DIRECTIONS = [
    { key: 'southbound', label: 'Southbound', help: 'Toward ATL from the north (most zones)' },
    { key: 'northbound', label: 'Northbound', help: 'Toward ATL from the south' },
];

// T13: arrival drop-off estimates only; arrival pickup times stay flight-driven.
export const CHART_SECTIONS = [
    { key: 'southbound', label: 'To the airport', help: 'Southbound: toward ATL from the north (most zones)' },
    { key: 'northbound', label: 'To the airport', help: 'Northbound: toward ATL from the south' },
    { key: 'from_airport', label: 'From the airport', help: 'Outbound from ATL. Used only for drop-off estimates on arrivals' },
];

export function clockMinutes(value) {
    const m = String(value || '').trim().match(/^(\d{1,2}):(\d{2})(?:\s*([AaPp])\.?[Mm]?\.?)?$/);
    if (!m) return NaN;
    let h = Number(m[1]); const min = Number(m[2]);
    if (min > 59 || h > 23 || (m[3] && (h < 1 || h > 12))) return NaN;
    if (m[3]) h = (h % 12) + (/p/i.test(m[3]) ? 12 : 0);
    return h * 60 + min;
}

export function formatClock(minutes) {
    const m = ((Math.round(minutes) % 1440) + 1440) % 1440;
    const h = Math.floor(m / 60), mm = String(m % 60).padStart(2, '0');
    return `${h % 12 || 12}:${mm} ${h < 12 ? 'AM' : 'PM'}`;
}

export const dayGroupOf = (weekday) => (weekday >= 1 && weekday <= 4 ? 'mon_thu' : 'fri_sun'); // 0 = Sunday

export function trafficAdd(windows = [], dayGroup, direction, startMinutes) {
    return windows.filter((w) => w.day_group === dayGroup && w.direction === direction)
        .filter((w) => clockMinutes(w.start_time) <= startMinutes && startMinutes <= clockMinutes(w.end_time))
        .reduce((max, w) => Math.max(max, Number(w.add_minutes) || 0), 0);
}

export function planPickup({ departure, weekday, international = false, buffers = {}, zone = null, windows = [] }) {
    const buffer = international ? buffers.international ?? 180 : buffers.domestic ?? 120;
    const step = Math.max(1, buffers.rounding ?? 5);
    const atAirport = clockMinutes(departure) - buffer;
    const base = zone && Number.isFinite(Number(zone.base_minutes)) && zone.base_minutes !== null && zone.base_minutes !== '' ? Number(zone.base_minutes) : null;
    if (base === null) {
        const drive = buffers.fallback_drive ?? 45;
        return { pickup: Math.floor((atAirport - drive) / step) * step, buffer, base: null, traffic: 0, drive, zoneUnknown: true };
    }
    const group = dayGroupOf(weekday), direction = zone.direction_to_airport || 'southbound';
    const add1 = trafficAdd(windows, group, direction, atAirport - base);
    const add2 = trafficAdd(windows, group, direction, atAirport - base - add1);
    const traffic = Math.max(add1, add2), drive = base + traffic;
    return { pickup: Math.floor((atAirport - drive) / step) * step, buffer, base, traffic, drive, zoneUnknown: false };
}

/** "A 4:30 PM domestic flight from a Zone 4 hotel on a Monday → pickup 1:25 PM" using the company's own zones and chart. */
export function workedExample({ zones = [], windows = [], buffers = {} } = {}) {
    const usable = zones.filter((z) => z.base_minutes !== null && z.base_minutes !== undefined && z.base_minutes !== '');
    const zone = usable.find((z) => /\b4\b/.test(z.name)) || usable[0];
    if (!zone) return 'Add a zone with its drive time to see a worked example.';
    const p = planPickup({ departure: '16:30', weekday: 1, buffers, zone, windows });
    const why = `${p.buffer / 60 === Math.round(p.buffer / 60) ? `${p.buffer / 60} h` : `${p.buffer} min`} airport cushion + ${p.base} min drive${p.traffic ? ` + ${p.traffic} min traffic` : ''}`;
    return `A 4:30 PM domestic flight from a ${zone.name} hotel on a Monday → pickup ${formatClock(p.pickup)} (${why}).`;
}
